import * as Logger from './logger.js'
import * as Utils from './utils.js'
import { HSGrid } from './grid.js';
import { HSComponent } from './component.js';
import styles from './staticgrid.css';
import template from './staticgrid.html';
import gridTemplate from './grid.html';
import cardTemplate from './card.html';
import { HSCard } from './card.js';
import { HSTable } from './table.js';
import { HSStatsTable } from './stats-table.js';
import { HSChart } from './chart.js';
import { HSMetric } from './metric.js';
import { HSLinkCard } from './link-card.js';
import { HSTiles } from './tiles.js';
import { HSEditor } from './editor.js';
import { HSViewPicker } from './view-picker.js';
import { HSTimePicker } from './time-picker.js';
import { HSSnapshotPicker } from './snapshot-picker.js';
import { HSStatsColumnPicker } from './stats-column-picker.js';
import { HSDateRangePicker } from './date-range-picker.js';
import { UserSession } from './session.js';

export class HSStaticGrid extends HSGrid {
    gridstack = null;
    reloadingTabGroups = false;
    responsiveLayoutRaf = null;

    constructor() { 
        super();
    }

    onLoad() {
        this.loadTemplate(template);
        this.loadStyles(styles);

        this.onEvent("hs-show-tab-content", (opts) => this.showTabContent(opts));
        this.onEvent('resize', () => this.scheduleResponsiveLayout(), window);
    }

    onUnload() {
        this.destroy();
    }

    destroy() {
        if (this.gridstack) {
            this.gridstack.remove();
            this.gridstack = null;
        }
        // clear stale DOM refs so the config can be safely passed to a new grid type
        this.cleanConfigRefs(this.config);
    }

    init(config) {
        super.init(config);

        // create our top-level gridstack container, within which we may have cards and other nested grids
        this.gridstack = this.shadowRoot.querySelector("#grid-container");
        this.config.element = this.gridstack;

        for (const grid of config?.grids || []) {
            if (this.isGridVisible(config, grid)) {
                this.addGrid(this.gridstack, grid);
            }
        }
        for (const card of config?.cards || []) {
            if (this.isConfigVisible(card)) {
                this.loadCard(this.gridstack, card);
            }
        }

        this.reloadTabGroups(this.shadowRoot);
        this.scheduleResponsiveLayout();
    }

    getResponsiveColumnCount(containerWidth = 0) {
        if (containerWidth >= 1320) { return 12; }
        if (containerWidth >= 1120) { return 10; }
        if (containerWidth >= 900) { return 8; }
        if (containerWidth >= 720) { return 6; }
        if (containerWidth >= 560) { return 4; }
        return 2;
    }

    getMountedWidgetConfig(widget) {
        if (!widget || !widget.isConnected) {
            return null;
        }
        // Widgets hidden by the report-view toggle must not compete for grid space.
        if (widget.style.display === 'none') {
            return null;
        }
        if (widget.classList?.contains('hs-grid')) {
            return widget.config || null;
        }
        const card = widget.querySelector('.card-body > *');
        return card?.config || null;
    }

    applyResponsiveLayoutToContainer(container, containerConfig) {
        if (!container || !containerConfig) {
            return;
        }

        const width = container.clientWidth || container.getBoundingClientRect().width || 0;
        const columns = this.getResponsiveColumnCount(width);
        container.style.gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;

        const widgets = Array.from(container.children || []);
        const entries = [];
        for (const widget of widgets) {
            const widgetConfig = this.getMountedWidgetConfig(widget);
            if (widgetConfig) {
                entries.push({ widget, widgetConfig });
            }
        }

        entries.sort((a, b) => {
            const ay = Number.isInteger(a.widgetConfig.y) ? a.widgetConfig.y : 0;
            const by = Number.isInteger(b.widgetConfig.y) ? b.widgetConfig.y : 0;
            if (ay !== by) {
                return ay - by;
            }
            const ax = Number.isInteger(a.widgetConfig.x) ? a.widgetConfig.x : 0;
            const bx = Number.isInteger(b.widgetConfig.x) ? b.widgetConfig.x : 0;
            return ax - bx;
        });

        // Default behavior: keep configured widths stable and wrap naturally.
        // Opt-in containers can enable shrink-to-min-width behavior below.
        if (!containerConfig?.responsiveShrinkToMinW) {
            let colCursor = 0;
            let rowCursor = 0;
            let rowHeight = 1;

            for (const { widget, widgetConfig } of entries) {
                const baseW = Math.max(1, Math.min(12, widgetConfig.w || 1));
                const spanW = Math.max(1, Math.min(columns, baseW));
                const spanH = Math.max(1, widgetConfig.h || 1);

                if (colCursor + spanW > columns) {
                    rowCursor += rowHeight;
                    colCursor = 0;
                    rowHeight = 1;
                }

                widget.style.gridColumnStart = colCursor + 1;
                widget.style.gridColumnEnd = `span ${spanW}`;
                widget.style.gridRowStart = rowCursor + 1;
                widget.style.gridRowEnd = `span ${spanH}`;

                colCursor += spanW;
                rowHeight = Math.max(rowHeight, spanH);
            }
            return;
        }

        const normalizeEntry = ({ widget, widgetConfig }) => {
            const preferredW = Math.max(1, Math.min(12, widgetConfig.w || 1));
            const configuredMinW = Number.isInteger(widgetConfig.minW) ? widgetConfig.minW : 1;
            const minimumW = Math.max(1, Math.min(preferredW, configuredMinW));
            const spanH = Math.max(1, widgetConfig.h || 1);
            const rowY = Number.isInteger(widgetConfig.y) ? widgetConfig.y : 0;
            return {
                widget,
                preferredW: Math.min(columns, preferredW),
                minimumW: Math.min(columns, minimumW),
                spanH,
                rowY,
            };
        };

        const shrinkRowWidths = (rowItems = []) => {
            const widths = rowItems.map(item => item.preferredW);
            let total = widths.reduce((sum, w) => sum + w, 0);
            let shrinkNeeded = Math.max(0, total - columns);
            if (shrinkNeeded < 1) {
                return widths;
            }

            while (shrinkNeeded > 0) {
                let shrunkThisPass = false;
                const byRoom = rowItems
                    .map((item, index) => ({ index, room: widths[index] - item.minimumW }))
                    .sort((a, b) => b.room - a.room);

                for (const { index, room } of byRoom) {
                    if (room > 0 && shrinkNeeded > 0) {
                        widths[index] -= 1;
                        shrinkNeeded -= 1;
                        shrunkThisPass = true;
                    }
                    if (shrinkNeeded < 1) {
                        break;
                    }
                }

                if (!shrunkThisPass) {
                    break;
                }
            }

            return widths;
        };

        const placeRow = (rowItems = [], rowCursor = 0) => {
            if (rowItems.length < 1) {
                return 0;
            }
            const widths = shrinkRowWidths(rowItems);
            let colCursor = 0;
            let rowHeight = 1;
            rowItems.forEach((item, index) => {
                const spanW = widths[index];
                item.widget.style.gridColumnStart = colCursor + 1;
                item.widget.style.gridColumnEnd = `span ${spanW}`;
                item.widget.style.gridRowStart = rowCursor + 1;
                item.widget.style.gridRowEnd = `span ${item.spanH}`;
                colCursor += spanW;
                rowHeight = Math.max(rowHeight, item.spanH);
            });
            return rowHeight;
        };

        let rowCursor = 0;
        let rowItems = [];
        let rowMinTotal = 0;
        let currentRowY = null;

        for (const entry of entries) {
            const item = normalizeEntry(entry);
            if (rowItems.length < 1) {
                rowItems.push(item);
                rowMinTotal = item.minimumW;
                currentRowY = item.rowY;
                continue;
            }

            // Keep configured y-groups on separate rows so lower content stays below.
            if (item.rowY !== currentRowY) {
                rowCursor += placeRow(rowItems, rowCursor);
                rowItems = [ item ];
                rowMinTotal = item.minimumW;
                currentRowY = item.rowY;
                continue;
            }

            if (rowMinTotal + item.minimumW <= columns) {
                rowItems.push(item);
                rowMinTotal += item.minimumW;
                continue;
            }

            rowCursor += placeRow(rowItems, rowCursor);
            rowItems = [ item ];
            rowMinTotal = item.minimumW;
            currentRowY = item.rowY;
        }

        if (rowItems.length > 0) {
            placeRow(rowItems, rowCursor);
        }
    }

    applyResponsiveLayout() {
        if (!this.gridstack || !this.isConnected) {
            return;
        }
        const containers = this.findChildren({
            node: this.shadowRoot,
            shadowOnly: false,
            predicate: child => child.classList?.contains('grid-container')
        });

        for (const container of containers) {
            const parentGrid = container.closest('.hs-grid');
            const containerConfig = parentGrid?.config || this.config;
            this.applyResponsiveLayoutToContainer(container, containerConfig);
        }
    }

    scheduleResponsiveLayout() {
        if (this.responsiveLayoutRaf) {
            cancelAnimationFrame(this.responsiveLayoutRaf);
        }
        this.responsiveLayoutRaf = requestAnimationFrame(() => {
            this.responsiveLayoutRaf = null;
            this.applyResponsiveLayout();
        });
    }

    clear(subgrid = undefined) {
        // close all grids and cards except those which are set with "alwaysVisible"
        if (subgrid) { Logger.verbose("clearing subgrid: ", subgrid.config); }
        const grids = this.findChildren({ predicate: child => child.classList.contains('hs-grid'), shadowOnly: false, node: subgrid ?? this.shadowRoot });
        for (const grid of grids) {
            if (grid.alwaysVisible) {
                Logger.debug("not clearing alwaysVisible grid: ", grid.config);
            } else {
                Logger.debug("closing grid: ", grid.config);
                this.closeGrid(null, grid, null);
            }
        }
    }

    setItemDimensions(widget, { x, y, w, h }) {
        // NOTE: we use a 0-based grid system from GridStack, but CSS Grid is 1-based, so we need to adjust the start positions
        widget.x = x !== undefined ? x : 0;
        widget.y = y !== undefined ? y : 0;
        widget.w = w !== undefined ? w : 1;
        widget.h = h !== undefined ? h : 1;
        widget.style.gridColumnStart = widget.x + 1; // CSS Grid is 1-based, so we add 1
        widget.style.gridRowStart = widget.y + 1; // CSS Grid is 1-based, so we add 1
        widget.style.gridColumnEnd = `span ${widget.w}`;
        widget.style.gridRowEnd = `span ${widget.h || 1}`;
    }

    addGrid(parentGrid, config) {
        if (!parentGrid) { parentGrid = this.shadowRoot.querySelector("#grid-container"); }
        if (config?.element?.isConnected) {
            return config.element;
        }
        if (config?.element && !config.element.isConnected) {
            config.element = null;
        }
        config.w = config.w || 6;
        config.grids = config.grids || [];
        config.cards = config.cards || [];
        const id = `grid-${this.gridIdCounter++}`;  // NOTE: id is not saved/loaded, it is auto-generated when we add to ensure unique
        Logger.info("adding subgrid: ", config);

        const subgrid = document.createElement('div');
        if (config.bgcolor == 'white') {
            subgrid.classList.add('hs-grid');
            subgrid.classList.add('hs-grid-white');
        } else {
            subgrid.classList.add('hs-grid');
            subgrid.classList.add('hs-grid-grey');
        }
        const el = document.createElement('template');
        Utils.setSafeHtml(el, Utils.evaluate(gridTemplate, this));
        const clone = el.content.cloneNode(true);
        subgrid.appendChild(clone);
        const gridContainer = subgrid.appendChild(document.createElement('div'));

        subgrid.alwaysVisible = config.alwaysVisible;
        subgrid.id = id;
        gridContainer.classList.add('grid-container');
        this.setItemDimensions(subgrid, config);

        if (config.headerPadding) {
            const hdr = subgrid.querySelector('.grid-header:not(.edit-mode)');
            if (hdr) { hdr.style.padding = config.headerPadding; }
        }
        if (config.gridPadding) {
            subgrid.style.paddingTop = config.gridPadding;
        }

        // reference the element from the config and vice versa
        subgrid.config = config;
        config.element = subgrid;

        parentGrid.appendChild(subgrid);

        // if this grid has a tabbar, add it now
        if (config.tabbar) {
            const gridHeader = subgrid.querySelector(".grid-header-left");
            gridHeader.innerHTML = '';
            const tabbar = document.createElement('div');
            tabbar.classList.add('tabbar-container');

            for (const grid of config?.grids || []) {
                const tab = document.createElement('div');
                tab.classList.add('tabbar-item');
                tab.textContent = grid.tabText;
                tab.id = grid.id;
                this.onEvent('click', () => {
                    if (!this.selected) {
                        this.reloadGridTabs(subgrid, grid.id);
                    }
                }, tab);
                tabbar.appendChild(tab);
            }

            gridHeader.appendChild(tabbar);

            this.removeSupplementalControls(subgrid);

            this.reloadGridTabs(subgrid);
        } else {
            // Track active view per subgrid so repeated clicks on the same view are no-ops.
            const userProviders = UserSession.get('providers') || [];
            const singleProviderUser = Array.isArray(userProviders) && userProviders.length === 1;
            const configuredDefaultView = config.defaultView || config.view;
            
            if (config.id === 'providers') {
                if (singleProviderUser) {
                    subgrid.__activeView = 'table';
                }
            }
            subgrid.__activeView = subgrid.__activeView || configuredDefaultView || 'tiles';
            // Initialize activeReportView from session or default to first picker option
            if (Array.isArray(config.reportPicker) && config.reportPicker.length > 0) {
                const saved = UserSession.get('activeReportView');
                const validSaved = config.reportPicker.some(r => r.id === saved);
                config.activeReportView = validSaved ? saved : config.reportPicker[0].id;
            }
            const hasStatsCard = (subgrid.config?.cards || []).some(cardConfig =>
                this.isConfigVisible(cardConfig) && cardConfig?.view === 'stats'
            );
            const controlPolicy = this.resolveSupplementalControls(config, {
                activeView: subgrid.__activeView,
                hasStatsCard,
                isTabbar: false,
            });
            this.bindGridHeader(subgrid, config, { eventTarget: subgrid });
            this.bindGridSearch(subgrid, {
                enabled: controlPolicy.search,
                onSearch: str => {
                    const cards = this.findChildren({ node: subgrid, predicate: child => child instanceof HSCard });
                    for (const card of cards) {
                        card.search(str);
                    }
                }
            });
            this.bindDateRangePicker(subgrid, { enabled: controlPolicy.dateRangePicker });
            this.bindViewPicker(subgrid, {
                enabled: controlPolicy.viewPicker,
                onViewChange: view => this.updateGridView(subgrid, view),
                activeView: subgrid.__activeView
            });
            this.setStatsControlsVisibility(subgrid, controlPolicy.statsControlsRow, hasStatsCard);
            this.bindTimeScopes(subgrid, {
                timeScopes: config.timeScopes,
                onTimeScope: timeScope => this.setTimeScope({ timeScope, subgrid })
            });
            const initialTimeScope = UserSession.get('timeScope') || this.config?.timeScopes?.[this.config.timeScopes.length - 1] || 'month';
            this.bindSnapshotPicker(subgrid, {
                enabled: controlPolicy.snapshotPicker,
                analysisWindow: initialTimeScope,
            });
            this.bindStatsColumnPresetPicker(subgrid, {
                enabled: controlPolicy.statsColumnPicker,
            });
            const initialTimeScopePicker = subgrid.querySelector('hs-time-picker');
            if (initialTimeScopePicker) {
                initialTimeScopePicker.style.display = controlPolicy.timePicker ? 'flex' : 'none';
            }
            // Bind report picker if the grid configures one (only relevant in stats view)
            this.bindReportPicker(subgrid, {
                reports: config.reportPicker,
                onReportChange: reportId => this.updateReportView(subgrid, reportId),
            });
            // Show the report-picker wrapper only when stats view is active
            const reportPickerWrapper = subgrid.querySelector('.report-picker-wrapper');
            if (reportPickerWrapper) {
                reportPickerWrapper.style.display =
                    subgrid.__activeView === 'stats' && Array.isArray(config.reportPicker) && config.reportPicker.length > 1
                        ? 'flex' : 'none';
                // Apply initial report-driven time-picker visibility
                if (subgrid.__activeView === 'stats' && config.activeReportView) {
                    this.applyReportPickerTimeScopeVisibility(subgrid, config.activeReportView);
                    // Apply report-specific grid title
                    this.applyReportPickerTitle(subgrid, config.activeReportView);
                }
            }

            this.setChildrenEditMode(subgrid, this.editMode);

            for (const grid of config?.grids || []) {
                if (this.isGridVisible(config, grid)) {
                    this.addGrid(gridContainer, grid);
                } else {
                    Logger.debug("skipping hidden grid: ", grid.config);
                }
            }
            if (config.cards) {
                config.cards.sort(this.widgetLayoutComparitor);
                for (const card of config?.cards || []) {
                    if (!this.isConfigVisible(card)) {
                        Logger.debug("skipping hidden card: ", card.config);
                    } else if (!this.matchesGridView({ ...config, view: subgrid.__activeView }, card)) {
                        Logger.debug("skipping card not matching active grid view: ", card.config);
                    } else {
                        this.loadCard(gridContainer, card);
                    }
                }
            }

            this.reloadTabGroups(subgrid);
            this.scheduleResponsiveLayout();
        }
    }

    loadCard(grid, config) {
        const card = super.loadCard(grid, config);
        this.scheduleResponsiveLayout();
        return card;
    }

    reloadGridTabs(subgrid, id = null) {
        const tabbar = subgrid.querySelector('.tabbar-container');
        if (!tabbar) { return; }
        const tabs = tabbar.querySelectorAll('.tabbar-item');
        if (tabs.length < 1) { return; }
        // choose the first tab by default to be selected if id is null
        if (id === null) {
            const displayed = (subgrid.config?.grids || []).find(g => g.displayed);
            id = displayed?.id || tabs[0].id;
        }

        // Persist visible tabbar content in config so reloadGrid() keeps exactly one child mounted.
        for (const candidate of subgrid.config?.grids || []) {
            candidate.displayed = candidate.id === id;
        }

        // set selected tab styles
        tabs.forEach(tab => {
            if (tab.id === id) {
                if (!tab.classList.contains('selected')) {
                    tab.classList.add('selected');
                    tab.classList.remove('clickable');
                }
            } else {
                tab.classList.add('clickable');
                tab.classList.remove('selected');
            }
        });
        // clear current content
        this.clear(subgrid);
        // add the selected grid as content
        const gridContainer = subgrid.querySelector(".grid-container");
        const grid = subgrid.config?.grids?.find(g => g.id === id);
        if (!grid) {
            Logger.warn("Could not find grid with id " + id + " to load for tab bar");
            return;
        }
        this.addGrid(gridContainer, grid);
        this.scheduleResponsiveLayout();
    }

    updateGridView(subgrid, view) {
        if (subgrid.__activeView === view) {
            return;
        }
        subgrid.__activeView = view;

        // close all grids and cards except those which are set with "alwaysVisible" or that match the current grid view selection
        // Also close any report-view cards that were hidden (display:none) but are still mounted
        const cards = this.findChildren({ node: subgrid, predicate: child => child instanceof HSCard });
        cards.forEach(card => {
            const viewMismatch = card.config?.view != view;
            const hiddenReportCard = !viewMismatch && card.config?.reportView && card.config.widget?.style.display === 'none';
            if (viewMismatch || hiddenReportCard) {
                Logger.debug("closing card not matching view: ", card);
                this.closeCard(null, card);
            }
        });
        subgrid.config?.cards?.forEach(cardConfig => {
            if (cardConfig?.view === view && this.matchesGridView({ ...subgrid.config, view }, cardConfig)) {
                Logger.debug("loading card with matching view: ", cardConfig);
                this.loadCard(subgrid.querySelector('.grid-container'), cardConfig);
            }
        });

        // Handle snapshot picker visibility based on view
        const snapshotPicker = subgrid.querySelector('hs-snapshot-picker');
        const timeScopePicker = subgrid.querySelector('hs-time-picker');
        const reportPickerWrapper = subgrid.querySelector('.report-picker-wrapper');
        const hasStatsCard = (subgrid.config?.cards || []).some(cardConfig =>
            this.isConfigVisible(cardConfig) && cardConfig?.view === 'stats'
        );
        const controlPolicy = this.resolveSupplementalControls(subgrid.config, {
            activeView: view,
            hasStatsCard,
            isTabbar: false,
        });
        this.setStatsControlsVisibility(subgrid, controlPolicy.statsControlsRow, hasStatsCard);
        if (snapshotPicker) {
            const timeScope = UserSession.get('timeScope') || this.config?.timeScopes?.[this.config.timeScopes.length - 1] || 'month';
            this.bindSnapshotPicker(subgrid, {
                enabled: controlPolicy.snapshotPicker,
                analysisWindow: timeScope,
            });
            this.bindStatsColumnPresetPicker(subgrid, {
                enabled: controlPolicy.statsColumnPicker,
            });
        }
        // If entering stats view, apply any report-picker-driven hide overrides
        if (view === 'stats' && subgrid.config?.activeReportView) {
            this.applyReportPickerTimeScopeVisibility(subgrid, subgrid.config.activeReportView);
        }
        // Show the report-picker dropdown only in stats view
        const hasReportPicker = Array.isArray(subgrid.config?.reportPicker) && subgrid.config.reportPicker.length > 1;
        if (reportPickerWrapper) {
            reportPickerWrapper.style.display = view === 'stats' && hasReportPicker ? 'flex' : 'none';
        }
        // Apply time-picker visibility: hide if the active report declares hideTimeScopes
        if (timeScopePicker) {
            if (controlPolicy.timePicker) {
                const activeReport = subgrid.config?.activeReportView;
                const reportHidesTime = view === 'stats' && activeReport
                    && (subgrid.config?.cards || []).some(c => c.reportView === activeReport && c.hideTimeScopes);
                timeScopePicker.style.display = reportHidesTime ? 'none' : 'flex';
            } else {
                timeScopePicker.style.display = 'none';
            }
        }
        /*
        const grids = this.findChildren({ predicate: child => child.classList.contains('hs-grid'), shadowOnly: true })
        Logger.debug("clearing grids: ", grids);
        for (const grid of grids) {
            if (grid.alwaysVisible) {
                Logger.debug("not clearing alwaysVisible grid: ", grid);
            } else {
                Logger.debug("closing grid: ", grid);
                this.closeGrid(null, grid, null);
            }
        }
        */
    }

    // Switch the active "report view" within a stats grid by hiding non-matching
    // cards and showing (or first-mounting) the matching one.
    // Cards are kept in the DOM so their SlickGrid canvas is never torn down,
    // eliminating the resize/repaint artifact that occurs after a destroy+recreate.
    updateReportView(subgrid, reportId) {
        if (!subgrid || !reportId) return;
        subgrid.config.activeReportView = reportId;

        const gridContainer = subgrid.querySelector('.grid-container');

        (subgrid.config?.cards || []).forEach(cardConfig => {
            // Only manage cards that participate in report-view switching
            if (!cardConfig?.reportView || cardConfig?.view !== subgrid.__activeView) return;

            const isMatch = cardConfig.reportView === reportId;

            if (isMatch) {
                if (cardConfig.element?.isConnected) {
                    // Already mounted — show the widget first, then re-emit the current
                    // row count so the header counter reflects this card immediately.
                    // (onRowCountChanged only fires when the count changes, so if the
                    // card already has the same number of rows it would stay stale.)
                    if (cardConfig.widget) cardConfig.widget.style.display = '';
                    const currentCount = cardConfig.element.dataView?.getLength?.();
                    if (typeof currentCount === 'number' && typeof cardConfig.element.emit === 'function') {
                        cardConfig.element.emit({ type: 'hs-data-count', count: currentCount });
                    }
                    if (typeof cardConfig.element.reloadData === 'function') {
                        cardConfig.element.reloadData();
                    }
                } else {
                    // First time this report card is shown — mount it
                    Logger.debug('mounting report card for first time: ', cardConfig.id);
                    this.loadCard(gridContainer, cardConfig);
                }
            } else if (cardConfig.widget?.isConnected) {
                // Hide non-matching mounted cards without destroying them
                Logger.debug('hiding report card for inactive reportView: ', cardConfig.id);
                cardConfig.widget.style.display = 'none';
            }
        });

        this.applyReportPickerTimeScopeVisibility(subgrid, reportId);
        this.applyReportPickerTitle(subgrid, reportId);
        this.scheduleResponsiveLayout();
    }

    // Update the grid title text to match the active report's configured title,
    // falling back to the grid's own config.title when no report-specific title is set.
    applyReportPickerTitle(subgrid, reportId) {
        const titleEl = subgrid?.querySelector('.grid-title');
        if (!titleEl) return;
        const reportDef = (subgrid.config?.reportPicker || []).find(r => r.id === reportId);
        titleEl.textContent = reportDef?.title ?? subgrid.config?.title ?? '';
    }

    // Show or hide the time-scope picker and snapshot picker based on whether the
    // active report card declares hideTimeScopes: true.
    applyReportPickerTimeScopeVisibility(subgrid, reportId) {
        const timeScopePicker = subgrid?.querySelector('hs-time-picker');
        const snapshotPicker = subgrid?.querySelector('hs-snapshot-picker');
        const reportCard = (subgrid.config?.cards || []).find(c => c.reportView === reportId);
        const hide = !!reportCard?.hideTimeScopes;
        if (timeScopePicker) {
            if (hide) {
                timeScopePicker.style.display = 'none';
            } else {
                // Restore only if the grid actually has time scopes configured
                const hasTimeScopes = Array.isArray(subgrid.config?.timeScopes) && subgrid.config.timeScopes.length > 0;
                timeScopePicker.style.display = hasTimeScopes ? 'flex' : 'none';
            }
        }
        if (snapshotPicker) {
            snapshotPicker.style.display = hide ? 'none' : 'flex';
        }
    }

    async reloadData() {
        // cascade through all grids/subgrids/cards, add or remove based on their displayIf conditions, and for the 
        // remaining ones, tell them to reload their data
        this.reloadGrid(this.config);

        // tell any other (not grids or cards) components to reload their data
        const components = this.findChildren({
            predicate: (child) => child instanceof HSComponent && !(child instanceof HSGrid) && !(child instanceof HSCard),
            shadowOnly: false,
        });
        for (const component of components) {
            if (typeof component.reloadData === "function") {
                component.reloadData();
            }
        }

        this.reloadTabGroups(this.shadowRoot);
    }

    reloadTabGroups(node = this.shadowRoot) {
        if (this.reloadingTabGroups) {
            return;
        }
        this.reloadingTabGroups = true;
        const cards = this.findChildren({ node, predicate: child => child instanceof HSComponent && child.config.tabGroup });
        Logger.debug('reloadTabGroups scope: ', {
            node_tag: node?.tagName,
            node_id: node?.id,
            node_config_id: node?.config?.id,
            cards_found: cards.length,
            card_ids: cards.map(card => card?.config?.id),
        });
        const tabGroups = new Set();
        for (const card of cards || []) {
            if (!tabGroups[card.config.tabGroup]) {
                tabGroups.add(card.config.tabGroup);
            }
        }
        Logger.debug('reloadTabGroups tabGroups: ', Array.from(tabGroups));
        try {
            for (const tabGroup of tabGroups) {
                this.showTabContent({ tabGroup });
            }
        } finally {
            this.reloadingTabGroups = false;
        }
    }

    async reloadGrid(config) {
        if (!config || !config.element) { return; }

        // find any grids or cards that need to be added or removed based on displayIf conditions, and 
        // for the remaining grids/cards tell them to reload their data
        Logger.debug("reloading grid with config: ", config);

        for (const grid of config.grids || []) {
            const visible = this.isGridVisible(config, grid);
            if (grid.displayIf) {
                if (Utils.filter(Utils.compileFilters(grid.displayIf, [ 'session' ]), [ UserSession ])) {
                    if (visible && !grid.element) {
                        // this subgrid needs to be loaded
                        const gridContainer = config.element == this.gridstack ? this.gridstack : config.element.querySelector('.grid-container');
                        this.addGrid(gridContainer, grid);
                    } else if (visible) {
                        // grid is already loaded, recurse 
                        await this.reloadGrid(grid);
                    }
                } else if (grid.element) {
                    Logger.debug("closing grid not matching displayIf condition: ", grid);
                    this.closeGrid(null, grid, null);
                }
            } else if (visible) {
                // recurse
                this.reloadGrid(grid);
            } else if (grid.element) {
                Logger.debug("closing hidden tab-controlled grid: ", grid);
                this.closeGrid(null, grid, null);
            }
        }

        for (const card of config.cards || []) {
            if (card.displayIf) {
                if (Utils.filter(Utils.compileFilters(card.displayIf, [ 'session' ]), [ UserSession ])) {
                    if (!card.element) {
                        // this card needs to be loaded, but only if it matches the currently active view
                        const activeView = config.element?.__activeView || config.defaultView || config.view || 'tiles';
                        if (!this.matchesGridView({ ...config, view: activeView }, card)) {
                            Logger.debug("skipping displayIf card not matching active view: ", card);
                        } else {
                            const gridContainer = config.element == this.gridstack ? this.gridstack : config.element.querySelector('.grid-container');
                            this.loadCard(gridContainer, card);
                        }
                    } else {
                        // card is already loaded, just reload data
                        card.element.reloadData();
                    }
                } else {
                    if (card.element) {
                        Logger.debug("closing card not matching displayIf condition: ", card);
                        this.closeCard(null, card.element);
                    } else {
                        Logger.debug("card is not loaded, so no need to close: ", card);
                    }
                }
            } else if (card.element && typeof card.element.reloadData === "function") {
                card.element.reloadData();
            }
        }
    }

    showTabContent({ id, tabGroup, container = null, content = null } = {}) {
        let subgrid = null;
        const requestedId = id;
        Logger.debug("showTabContent: ", arguments[0]);
        tabGroup = this.resolveTabGroup(tabGroup, id);
        if (!tabGroup) {
            if (id) {
                // Fallback: no mounted tab card found for this id (e.g. the link-card replaced the
                // metric tab in a single-provider scenario). Directly find and mount the content grid.
                const found = this.findGridWithParent(id);
                if (found) {
                    const { config: gridConfig, parentConfig } = found;
                    gridConfig.displayed = true;
                    if (!gridConfig.element?.isConnected) {
                        const parentElement = parentConfig.element;
                        const gridContainer = parentElement === this.gridstack
                            ? this.gridstack
                            : parentElement?.querySelector('.grid-container');
                        if (gridContainer) {
                            this.addGrid(gridContainer, gridConfig);
                            return;
                        }
                    }
                    return;
                }
            }
            Logger.warn("Cannot show tab content, missing tabGroup", { id, container, content });
            return;
        }
        // get the selected tab
        let tab = null;
        if (id) {
            tab = this.findTabById(tabGroup, id);
        } else {
            const rememberedId = this.getRememberedTabId(tabGroup);
            if (rememberedId) {
                tab = this.findTabById(tabGroup, rememberedId);
            }
            // see if any visible tabs are already selected, if so then no action needed
            if (!tab) {
                tab = this.findActiveTab(tabGroup);
            }
            if (!tab) {
                tab = this.pickDefaultTab(tabGroup);
            }
            id = tab?.config?.id;
        }

        // if the current tab is already selected, then return - no action needed
        if (tab && tab.selected && requestedId) {
            Logger.verbose("active tab already selected, returning");
            return;
        }
        if (!tab) {
            Logger.warn("no tab found for tabgroup: ", { id, tabGroup, container, content });
            return;
        }

        // get the id of the parent container grid (if any), else we default to the main gridstack itself
        if (!container && tab.config?.onclick?.action == 'showTabContent') {
            container = tab.config.onclick.container;
        }
        if (container) {
            // lookup the container grid by id
            const grids = this.findChildren({ predicate: child => child.classList.contains('hs-grid') && child.config.id == container, shadowOnly: false });
            if (grids.length < 1) {
                Logger.warn("can't find tab container: ", container);
                return;
            }
            subgrid = grids[0];
        }
        // get the id of the content grid to display, else default to the same id as the tab
        if (!content && tab.config?.onclick?.action == 'showTabContent') {
            content = tab.config.onclick.content;
        }
        if (!content) {
            content = tab.config.id;
            if (!content) {
                Logger.warn("no content id for tabGroup: ", tabGroup);
                return;
            }
        }
        const parentConfig = subgrid?.config || this.config;
        const parentGrid = subgrid || this;
        const grid = parentConfig?.grids?.find(g => g.id === content);
        if (!grid) {
            Logger.warn("Could not find content with id " + content + " to load for tab group");
            return;
        }

        // Persist active tab content visibility so reloadGrid() keeps the selected content mounted.
        for (const candidate of parentConfig?.grids || []) {
            if (this.isTabControlledGrid(parentConfig, candidate)) {
                candidate.displayed = candidate.id === content;
            }
        }
        const contentMounted = !!(grid.element && grid.element.isConnected);
        if (tab.selected && contentMounted) {
            this.rememberTabSelection(tabGroup, id);
            return;
        }
        Logger.debug("selecting tab for tabgroup: ", { id, tabGroup, tab: tab.config, content, container, subgrid, contentMounted });
        // clear the content within the container grid (other than those which have "alwaysVisible" = true)
        this.clear(subgrid);
        // unselect all tabs in the tab group
        this.unselectAllTabs(tabGroup);
        // select the tab with the given id
        this.selectTab(id, tabGroup);
        this.rememberTabSelection(tabGroup, id);
        const gridContainer = subgrid ? subgrid.querySelector(".grid-container") : null;
        this.addGrid(gridContainer, grid);
    }
    
    getWidgetLayout(widget) {
        return Utils.pickIfDefined(widget, 'x', 'y', 'w', 'h');
    }

    addCard(id, grid, config) {
        config.w = config.w || 2;
        config.h = config.h || 2;
        Logger.debug("adding card with original dimensions: ", config);

        const el = document.createElement('template');
        Utils.setSafeHtml(el, Utils.evaluate(cardTemplate, this));
        const card = grid.appendChild(document.createElement('div'));
        card.classList.add('grid-item');
        card.appendChild(el.content.cloneNode(true));
        card.id = 'node-' + id;

        this.setItemDimensions(card, config);

        grid.appendChild(card);
        return card;
    }

    drawCardContent(grid, widget, id, config) {
        // merge/inherit existing card widget dimensions but allow new config to override
        Object.assign(config, this.getWidgetLayout(widget));
        Logger.debug("applying card config: ", config);

        // remove any previous card content (if any)
        const card_body = widget.querySelector(".card-body");
        while (card_body.hasChildNodes()) {
            card_body.removeChild(card_body.firstChild);
        }
        const card = document.createElement(config.type);
        card.setAttribute('id', id);
        card_body.appendChild(card);
        //widget.querySelector(".card-title").textContent = `${cardState.id} - ${cardState.config.title}`;

        const prevButton = widget.querySelector('#prev-button');
        prevButton.classList.add('hide');

        const applyButton = widget.querySelector('#apply-button');
        applyButton.classList.add('hide');

        const editButton = widget.querySelector('#edit-json');
        editButton.classList.add('hide');

        const closeButton = widget.querySelector('#close-button');
        closeButton.classList.add('hide');

        card.init(config);
        card.editMode = this.editMode;
        this.setChildrenEditMode(card, this.editMode);

        // reference the element from the config
        config.element = card;
        config.widget = widget; // need this for removing the card from the grid

        setTimeout(() => this.redrawCard(id), 300);
        return card;
    }

    toggleEditMode(enabled) {
        super.toggleEditMode(false);
    }

    editCard(grid, widget, id, config) {
        // not supported
    }

    configSaved() {
        // TODO: we can disable the "save" button now, until new changes are made
    }

    closeGrid(parentGrid, grid, widget) {
        const gridElement = (grid instanceof HTMLElement) ? grid : grid?.element;
        const gridConfig = gridElement?.config || (grid && typeof grid === 'object' ? grid : null);

        // remove references
        if (gridConfig) {
            gridConfig.element = null;
        }
        if (gridElement?.config) {
            gridElement.config = null;
        }
        if (gridElement?.remove) {
            gridElement.remove();
        }
        this.scheduleResponsiveLayout();
    }

    closeCard(grid, card) {
        Logger.debug("closing card: ", card);
        card.onClose();
        // remove references
        if (card.config) {
            const widget = card.config.widget;
            card.config.element = null;
            card.config = null;
            if (widget) {
                widget.remove();
            }
        }
        this.scheduleResponsiveLayout();
    }
}
customElements.define('hs-staticgrid', HSStaticGrid);
